import React, { useState, useEffect, useCallback } from 'react';
import { TAGLEVELS, TAGTYPE } from '../utils/Constants';
import { useApiCaller } from '../utils/ApiUtilities';
import { getTagUrl, putTagUrl, getTagReferencesUrl } from '../utils/UrlUtilities';
import { constructTagPayload, constructTagReferencesPayload } from '../utils/PayloadUtils';
import CloseButton from './ui/CloseButton';

// --- Mocked Dependencies for Standalone Component ---
// In a real project, these would be imported from their respective files.

const Input = ({ type, placeholder, value, onChange, className, required, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, disabled }) => (
    <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`form-control ${className || ''}`}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        disabled={disabled} // Added disabled prop
    />
);

export default function TagEditForm({ tag, onCancel, onSave }) {
    const [tagName, setTagName] = useState(''); // Initialize empty, will be set in useEffect
    const [level, setLevel] = useState(tag.level || ''); // Initialize empty
    const [type, setType] = useState(tag.tag_type || ''); // Initialize empty
    const [subject, setSubject] = useState('');
    const [availableSubjects, setAvailableSubjects] = useState([]);
    const [isCurrent, setIsCurrent] = useState(tag.is_current !== false);

    // Reference counts (questions + quizzes) — when > 0, level/type/subject are locked
    // (changing them would orphan references); renaming is allowed and cascades server-side.
    const [refs, setRefs] = useState(null); // { question_count, quiz_count } | null while loading
    const referenced = !!refs && ((refs.question_count || 0) + (refs.quiz_count || 0) > 0);
    // Subject is only frozen when the tag already has one; an empty subject stays editable.
    const subjectLocked = referenced && !!tag.subject;

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const CallApi = useApiCaller();

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const response = await CallApi({
                    url: getTagReferencesUrl(),
                    method: 'POST',
                    payload: constructTagReferencesPayload([tag.tag_id]),
                    token: true,
                });
                if (!active) return;
                const row = (response.success && response.data?.references?.[0]) || null;
                setRefs(row ? { question_count: row.question_count || 0, quiz_count: row.quiz_count || 0 } : { question_count: 0, quiz_count: 0 });
            } catch (err) {
                if (active) setRefs({ question_count: 0, quiz_count: 0 });
            }
        })();
        return () => { active = false; };
    }, [tag.tag_id]);

    const fetchSubjects = async () => {
        if (level && level !== "SELECT") {
            try {
                const url = getTagUrl({ level: level, tag_type: "SUBJECT" });
                const response = await CallApi({
                    url: url,
                    method: 'GET',
                    token: true,
                    payload: null
                });
                if (response.success) {
                    const uniqueSubjects = [...new Set(response.data.tags.map(t => t.name))];
                    setAvailableSubjects(uniqueSubjects);
                } else {
                    setAvailableSubjects([]);
                    console.error("Failed to fetch subjects:", response.message);
                }
            } catch (err) {
                setAvailableSubjects([]);
                console.error("Error fetching subjects:", err);
            }
        } else {
            setAvailableSubjects([]);
        }
    };

    useEffect(() => {
        if (type === "TOPIC") {
            // Prefer the first-class subject field; derive the bare topic name by stripping the
            // "Subject-" prefix. Fall back to the legacy name-split for older rows.
            if (tag.subject) {
                setSubject(tag.subject);
                const prefix = `${tag.subject}-`;
                setTagName(tag.name && tag.name.startsWith(prefix) ? tag.name.slice(prefix.length) : (tag.name || ''));
            } else if (tag.name.includes('-')) {
                const parts = tag.name.split('-');
                setSubject(parts[0]);
                setTagName(parts.slice(1).join('-')); // TagName is the part after the first dash
            } else {
                setSubject('');
                setTagName(tag.name || '');
            }
            fetchSubjects();
        } else {
            setSubject('');
            setAvailableSubjects([]);
        }
    }, [level, type]);


    const handleSave = async () => {
        setError('');

        if (!tagName || !level || level === "SELECT" || !type || type === "SELECT") {
            setError('Please fill all required fields (Tag Name, Level, Type).');
            return;
        }

        if (type === "TOPIC" && (!subject || subject === "SELECT")) {
            setError('Please select a Subject for TOPIC type tags.');
            return;
        }

        setIsSaving(true);
        try {
            let finalTagName = tagName;
            if (type === "TOPIC") {
                finalTagName = `${subject}-${tagName}`;
            }

            const tagSubject = type === "TOPIC" ? subject : undefined;
            const updatedTagPayload = constructTagPayload(finalTagName, level, type, undefined, isCurrent, tagSubject);
            console.log("put payload : ", updatedTagPayload);
            const putTagResponse = await CallApi({
                url: putTagUrl(tag.tag_id),
                method: 'PUT',
                payload: updatedTagPayload,
                token: true,
            });

            if (putTagResponse.success) {
                setTimeout(() => {
                    onSave(putTagResponse.data);
                }, 500);
            } else {
                setError(putTagResponse.message || 'Failed to update tag.');
                console.error('Update Tag Error:', putTagResponse);
            }
        } catch (err) {
            setError('An error occurred while updating the tag.');
            console.error('Update Tag Error in catch block:', err);
        } finally {
            setIsSaving(false);
        }
    };

    let isFormValid = tagName && level && level !== "SELECT" && type && type !== "SELECT";
    if (type === "TOPIC") {
        isFormValid = isFormValid && subject && subject !== "SELECT";
    }

    return (
        // Bootstrap Modal Structure
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-md" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title fs-5 fw-bold text-dark">Edit Tag (ID: {tag?.tag_id})</h5>
                        <CloseButton onClick={onCancel} />
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger mb-3" role="alert">{error}</div>}

                        {referenced && (
                            <div className="alert alert-info mb-3" role="alert">
                                This tag is used by <strong>{refs.question_count}</strong> question(s) and <strong>{refs.quiz_count}</strong> test(s).
                                {subjectLocked ? ' Level, Type and Subject are locked.' : ' Level and Type are locked.'} Renaming is allowed and will automatically rename every question and test that uses it.
                            </div>
                        )}

                        <form onSubmit={(e) => e.preventDefault()}>
                            <div className="mb-3">
                                <label htmlFor="tagName" className="form-label text-gray-700">Tag Name <span className="text-danger">*</span></label>
                                <Input type="text" id="tagName" value={tagName} onChange={(e) => setTagName(e.target.value)} required />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="tagLevel" className="form-label text-gray-700">Tag Level <span className="text-danger">*</span></label>
                                <select id="tagLevel" value={level} onChange={(e) => setLevel(e.target.value)} className="form-select" required disabled={referenced} >
                                    {TAGLEVELS.map((item) => (
                                        item.submenu ? (
                                            <optgroup key={item.value} label={item.label}>
                                                {item.submenu.map((sub) => (
                                                    <option key={sub.value} value={sub.value}>{sub.label}</option>
                                                ))}
                                            </optgroup>
                                        ) : (
                                            <option key={item.value} value={item.value} disabled={item.value === "" && item.label === "SELECT"}>
                                                {item.label}
                                            </option>
                                        )
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label htmlFor="tagType" className="form-label text-gray-700">Tag Type <span className="text-danger">*</span></label>
                                <select id="tagType" value={type} onChange={(e) => setType(e.target.value)} className="form-select" required disabled={referenced} >
                                    {TAGTYPE.map((tagTypeOption) => (
                                        <option key={tagTypeOption} value={tagTypeOption} disabled={tagTypeOption === "SELECT"}>
                                            {tagTypeOption}
                                        </option>
                                    ))}
                                </select>
                            </div>
                             {/* New: Conditional Subject Dropdown for Edit */}
                            {type === "TOPIC" && (
                                <div className="mb-3">
                                    <label htmlFor="tagSubject" className="form-label text-gray-700">Subject <span className="text-danger">*</span></label>
                                    <select
                                        id="tagSubject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="form-select"
                                        required
                                        disabled={subjectLocked || !level || availableSubjects.length === 0}
                                    >
                                        <option value="" disabled>SELECT</option>
                                        {availableSubjects.length > 0 ? (
                                            availableSubjects.map((subName) => (
                                                <option key={subName} value={subName}>
                                                    {subName}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>No subjects available for this level</option>
                                        )}
                                    </select>
                                    {!level && <p className="text-muted small mt-1">Select a level first to see subjects.</p>}
                                    {level && availableSubjects.length === 0 && <p className="text-muted small mt-1">No subjects found for {level}.</p>}
                                </div>
                            )}
                            {(type === 'SUBJECT' || type === 'TOPIC') && (
                                <div className="form-check mt-3">
                                    <input className="form-check-input" type="checkbox" id="tagIsCurrent" checked={isCurrent} onChange={(e) => setIsCurrent(e.target.checked)} />
                                    <label className="form-check-label" htmlFor="tagIsCurrent">
                                        In current syllabus
                                    </label>
                                    <div className="form-text">
                                        Uncheck to retire this {type.toLowerCase()} — it's hidden from new test builders. Then archive its questions from the Question grid (filter by this {type.toLowerCase()} → Set syllabus → Outdated).
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                    <div className="modal-footer d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSaving}>Cancel</button>
                        <button type="submit" className="btn btn-primary" onClick={handleSave} disabled={!isFormValid || isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
// export default TagEditForm;
